import { matchPath } from '@umijs/max';
import appRoutes from '../../config/routes';

/**
 * 递归对菜单进行排序（按 order 字段升序）
 * @param menus 菜单列表
 * @returns 排序后的菜单列表
 */
export const sortMenusByOrder = (menus: any[]): any[] => {
  if (!menus || !Array.isArray(menus)) return [];

  return [...menus]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((menu) => {
      if (menu.children && Array.isArray(menu.children)) {
        return {
          ...menu,
          children: sortMenusByOrder(menu.children),
        };
      }
      return menu;
    });
};

/**
 * 根据路径获取路由信息
 * @param pathname 路径
 * @param routes 路由配置
 * @param prefix key 前缀
 * @param parentPath 父级路径
 */
export const getRouteInfo = (
  pathname: string,
  routes: any[],
  prefix = 'menu',
  parentPath = '',
): {
  key: string;
  params: any;
  access?: string;
  name?: string;
  pathPattern?: string;
  keepAliveName?: string;
  keepAlive?: boolean;
  hideInMenu?: boolean;
  component?: string;
  hasChildren?: boolean;
  redirect?: string;
  [key: string]: any;
} | null => {
  if (!pathname || !routes) return null;

  for (const route of routes) {
    // 如果路由有 name，则拼接到 prefix
    const currentKey = route.name ? `${prefix}.${route.name}` : prefix;

    // 构建当前路由的完整路径
    let currentFullPath = route.path;

    if (typeof route.path === 'string') {
      if (route.path.startsWith('/')) {
        // 绝对路径
        currentFullPath = route.path;
      } else {
        // 相对路径，拼接父路径
        // 处理 parentPath 结尾 / 的情况，避免双斜杠，但也不要丢失 /
        const separator = parentPath.endsWith('/') ? '' : '/';
        // 如果 parentPath 为空，separator 为 /，结果是 /path
        // 如果 parentPath 是 /，separator 为 ''，结果是 /path
        currentFullPath = `${parentPath}${separator}${route.path}`;
      }
    } else if (route.path === undefined && route.routes) {
      // 某些布局路由可能没有 path，继承父路径
      currentFullPath = parentPath;
    }

    // 确保路径以 / 开头（除非是 *）
    // 其实 matchPath 可以处理

    // 尝试匹配当前路由
    if (currentFullPath) {
      const match = matchPath({ path: currentFullPath, end: true }, pathname);
      if (match) {
        return {
          ...route,
          key: currentKey,
          params: match.params,
          access: route.access,
          name: route.name,
          pathPattern: currentFullPath,
          keepAliveName: route.keepAliveName,
          keepAlive: route.keepAlive,
          hideInMenu: route.hideInMenu,
          component: route.component,
          hasChildren: !!(route.routes && route.routes.length > 0),
          redirect: route.redirect,
        };
      }
    }

    // 递归查找子路由
    if (route.routes) {
      const found = getRouteInfo(pathname, route.routes, currentKey, currentFullPath || parentPath);
      if (found) return found;
    }
  }
  return null;
};

/**
 * 获取路由的国际化 key
 * @param path 路径
 * @param name 默认名称
 */
export const getRouteKey = (path: string | undefined, name: string): string => {
  if (!path) return name;
  const routeInfo = getRouteInfo(path, appRoutes);
  return routeInfo?.key || name;
};
